import React, { useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { MainAction } from "@/components/MainAction";
import { EmptyState } from "@/components/EmptyState";
import { DeliveryPicker } from "@/components/DeliveryPicker";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useBackButton } from "@/lib/hooks";
import { ApiError, claimGift, fetchGift, type GiftView } from "@/lib/api";
import { canRequestPhone, getTelegramUser, haptic, requestPhone } from "@/lib/telegram";

/** Recipient side of "Подарить кофе": see the gift, choose pickup or delivery, date and time. */
export function GiftPage({ code }: { code: string }) {
  const { t } = useI18n();
  const { navigate } = useRouter();
  useBackButton(() => navigate({ name: "home" }));
  const u = getTelegramUser();
  const [gift, setGift] = useState<GiftView | null | undefined>(undefined);
  const [method, setMethod] = useState<"pickup" | "yandex">("yandex");
  const [fullName, setFullName] = useState([u?.first_name, u?.last_name].filter(Boolean).join(" "));
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [comment, setComment] = useState("");
  const [when, setWhen] = useState<{ date?: string; slot?: string }>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => fetchGift(code).then(setGift).catch(() => setGift(null));
  useEffect(() => {
    load();
  }, [code]);

  if (gift === undefined) return (<><TopBar title={t("gift.title")} /><main className="page page--no-nav">{t("common.loading")}</main></>);
  if (!gift) return (<><TopBar title={t("gift.title")} /><main className="page page--no-nav"><EmptyState art="favorites" title={t("gift.notFound")} /></main></>);

  const claim = async () => {
    if (!fullName.trim() || phone.replace(/\D/g, "").length < 9 || (method === "yandex" && !address.trim())) {
      setError(t("gift.fillAll"));
      haptic("error");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await claimGift(code, { fullName: fullName.trim(), phone: phone.trim(), deliveryMethod: method, address: method === "yandex" ? address.trim() : undefined, comment: comment.trim() || undefined, deliveryDate: when.slot ? when.date : undefined, deliverySlot: when.slot });
      haptic("success");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TopBar title={t("gift.title")} />
      <main className="page page--no-nav">
        <div className="gift-hero">
          <span className="gift-hero__bow">🎁</span>
          <span className="eyebrow">{t("gift.eyebrow")}</span>
          <h1>{gift.fromName ? t("gift.fromName", { name: gift.fromName }) : t("gift.forYou")}</h1>
          {gift.message && <p className="gift-hero__msg">«{gift.message}»</p>}
          <div className="gift-hero__items">
            {gift.items.map((i, k) => (
              <div key={k}>
                {i.productName}
                {i.variantLabel !== "Стандарт" ? ` · ${i.variantLabel}` : ""} × {i.quantity}
              </div>
            ))}
          </div>
        </div>

        {!gift.paid ? (
          <p className="notice">{t("gift.notPaid")}</p>
        ) : gift.claimed ? (
          <div className="arrive-card">
            <strong>◆ {t("gift.claimedTitle")}</strong>
            <span>{gift.deliveryMethod === "pickup" ? t("gift.claimedPickup") : t("gift.claimedDelivery", { when: gift.deliverySlot ?? "" })}</span>
          </div>
        ) : gift.isBuyer ? (
          <p className="notice">{t("gift.buyerView")}</p>
        ) : (
          <>
            <div className="section-heading"><span className="diamond" />{t("gift.how")}</div>
            <div className="segmented" style={{ marginBottom: 16 }}>
              <button className="chip" data-active={method === "yandex"} onClick={() => setMethod("yandex")}>{t("gift.delivery")}</button>
              <button className="chip" data-active={method === "pickup"} onClick={() => setMethod("pickup")}>{t("gift.pickup")}</button>
            </div>
            <div className="field"><label>{t("checkout.fullName")}</label><input value={fullName} onChange={(e) => setFullName(e.target.value)} /></div>
            {canRequestPhone() && (
              <button className="contact-btn" onClick={async () => { const p = await requestPhone(); if (p) setPhone(p); }}>{t("growth.sharePhone")}</button>
            )}
            <div className="field"><label>{t("checkout.phone")}</label><input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+998 90 123 45 67" /></div>
            {method === "yandex" ? (
              <>
                <div className="field"><label>{t("checkout.address")}</label><input value={address} onChange={(e) => setAddress(e.target.value)} /></div>
                <DeliveryPicker value={when} onChange={setWhen} />
              </>
            ) : (
              <p className="notice">{t("checkout.pickupAddress")} · {t("checkout.pickupHours")}</p>
            )}
            <div className="field"><label>{t("checkout.comment")}</label><textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} /></div>
            {error && <div className="form-error-banner">{error}</div>}
            <MainAction text={t("gift.claim")} onClick={claim} loading={busy} />
          </>
        )}
      </main>
    </>
  );
}
